import { useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { LoadMoreButton } from "@rio-cloud/rio-uikit";

import { IBudget } from "../../../../models/budgetV2";
import { IKanbanColumn } from "../utils/columns";
import { IKanbanColumnData } from "../hooks/useBudgetKanban";

import KanbanBoardSidebar from "./budgetKanbanBoardSidebar";
import BudgetKanbanCard from "./budgetKanbanCard";
import BudgetKanbanSkeletonCard from "./budgetKanbanSkeletonCard";

interface BudgetKanbanBoardProps {
  persona?: string;
  loading: boolean;
  columns: IKanbanColumn[];
  columnsData: Record<string, IKanbanColumnData>;
  viewType: "geral" | "cliente" | "fabricante";

  setViewType: (viewType: "geral" | "cliente" | "fabricante") => void;
  onViewVersions?: (budget: IBudget) => void;
  loadMoreBudgets: () => void;
}

const BudgetKanbanBoard = ({
  persona,
  loading,
  columns,
  columnsData,
  viewType,

  setViewType,
  onViewVersions,
  loadMoreBudgets,
}: BudgetKanbanBoardProps) => {
  const intl = useIntl();

  const headerRef = useRef<HTMLDivElement>(null);
  const [stickyTop, setStickyTop] = useState(0);

  useLayoutEffect(() => {
    let el: HTMLElement | null = headerRef.current?.parentElement ?? null;
    while (el) {
      const { overflowY } = window.getComputedStyle(el);
      if (overflowY === "auto" || overflowY === "scroll") break;
      el = el.parentElement;
    }
    if (!el) return;
    setStickyTop(-parseFloat(window.getComputedStyle(el).paddingTop || "0"));
  }, []);

  const columnsDataList = Object.values(columnsData);
  const loadedBudgets = columnsDataList.reduce((sum, data) => sum + data.budgets.length, 0);
  const totalBudgets = columnsDataList.reduce((sum, data) => sum + data.total, 0);
  const isLoadingMore = columnsDataList.some((data) => data.loadingMore);

  return (
    <div className="flex flex-col gap-4 min-w-[1024px] max-w-[2048px] mx-auto">
      {false && (
        <KanbanBoardSidebar
          viewType={viewType}
          setViewType={setViewType}
        />
      )}


      <div className="flex-1 bg-white p-3">
        <div className="relative w-full select-none">
          <div ref={headerRef} style={{ top: stickyTop }} className="grid gap-0 grid-cols-3 sticky z-10">
            {columns.map((column) => (
              <div key={column.key} className="flex-1 px-0">
                <div className="bg-[#C7D1D9] px-4 py-3 flex justify-between items-center">
                  <h3 className="text-size-14 font-semibold text-gray-900 m-0 uppercase">
                    {column.label}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          <div className="grid gap-0 grid-cols-3 items-stretch">
            {columns.map((column) => {
              const data = columnsData[column.key];
              const columnBudgets = data?.budgets ?? [];

              return (
                <div key={`${column.key}-stack`} className="p-0 bg-[#D0DAE2] min-h-[240px]">
                  {loading ? (
                    <>
                      <BudgetKanbanSkeletonCard />
                      <BudgetKanbanSkeletonCard />
                    </>
                  ) : (
                    columnBudgets.map((budget) => (
                      <BudgetKanbanCard key={budget.uuid} budget={budget} onViewVersions={onViewVersions} />
                    ))
                  )}
                  {data?.loadingMore && <BudgetKanbanSkeletonCard />}

                </div>
              );
            })}
          </div>


          <div className="w-full flex justify-center items-center p-8">
            {!loading && !isLoadingMore && totalBudgets > 0 && (
              <LoadMoreButton
                loaded={loadedBudgets}
                total={totalBudgets}
                onLoadMore={loadMoreBudgets}
                loadMoreMessage={intl.formatMessage({ id: "intl-msg:budgets.kanban.loadMore" })}
                noMoreMessage={intl.formatMessage({ id: "intl-msg:budgets.kanban.noMore" })}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetKanbanBoard;
